/**
 * API service for backend communication
 */
import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '../config/api';
import type { SearchRequest, SearchResponse, SearchResult } from '../types/api';

class ApiService {
    private client: AxiosInstance;

    constructor() {
        this.client = axios.create({
            timeout: 30000,
        });

        // Update base URL when backend changes
        this.updateBaseUrl();
    }

    updateBaseUrl(): void {
        this.client.defaults.baseURL = apiConfig.getBaseUrl();
    }

    async healthCheck(): Promise<{ status: string }> {
        const response = await this.client.get('/health');
        return response.data;
    }

    async searchNews(request: SearchRequest): Promise<SearchResponse> {
        const response = await this.client.post<SearchResponse>('/search', request);
        return response.data;
    }

    async getResults(
        keywords?: string,
        date?: string,
        limit: number = 50
    ): Promise<SearchResult[]> {
        const params: Record<string, string | number> = { limit };
        if (keywords) params.keywords = keywords;
        if (date) params.date = date;

        const response = await this.client.get<SearchResult[]>('/results', { params });
        return response.data;
    }

    async getResultById(searchId: string): Promise<SearchResult> {
        const response = await this.client.get<SearchResult>(`/results/${searchId}`);
        return response.data;
    }

    async getLatestResults(limit: number = 20): Promise<SearchResult[]> {
        const response = await this.client.get<SearchResult[]>('/results/latest', {
            params: { limit },
        });
        return response.data;
    }
}

export const apiService = new ApiService();

const apiInstance: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1',
    timeout: 60000,  // 60 Sekunden für lange Search-Anfragen
    headers: {
        'Content-Type': 'application/json',
    },
});

apiInstance.interceptors.response.use(
    response => response,
    error => {
        console.error('API Error:', {
            url: error.config?.url,
            baseURL: error.config?.baseURL,
            message: error.message
        });
        return Promise.reject(error);
    }
);

export const api = apiInstance;
